/**
 * Central content/data source for the Membertou Boxing Club site.
 *
 * Editing site copy, nav, contact info, or routes? Do it here so it stays
 * consistent across pages.
 */

export type NavItem = {
  label: string;
  href: string;
  /**
   * When true, the link uses startsWith matching for active state instead of
   * exact match (useful for sections that have child routes).
   */
  matchPrefix?: boolean;
};

export const site = {
  name: "Membertou Boxing Club",
  tagline: "Where Warriors Train",
  url: "https://membertouboxing.com",
  description:
    "Community boxing club in Membertou, Nova Scotia. Free training for all ages and experience levels. Where Warriors Train.",
  contact: {
    address: {
      street: "113 Membertou Street",
      cityRegion: "Membertou, NS B1S 2M9",
      mapsQuery: "113 Membertou Street, Membertou, NS B1S 2M9",
    },
    phone: "902.217.6694",
    phoneHref: "tel:+19022176694",
    email: "boxingclub@membertou.ca",
    emailHref: "mailto:boxingclub@membertou.ca",
    facebookLabel: "Membertou Boxing Club on Facebook",
    facebookHref: "https://www.facebook.com/groups/47241387972/",
  },
  trainingHours: "Monday to Thursday, 6:00pm – 8:00pm",
} as const;

export const primaryNav: NavItem[] = [
  { label: "Home", href: "/" },
  { label: "About Us", href: "/about" },
  { label: "Training", href: "/training" },
  { label: "Contact", href: "/contact" },
];

export const footerNav: NavItem[] = [
  { label: "Home", href: "/" },
  { label: "About", href: "/about" },
  { label: "Contact Us", href: "/contact" },
];

export type FeeRow = {
  category: string;
  amount: string;
  note?: string;
};

export const fees: FeeRow[] = [
  {
    category: "Competitive Boxing",
    amount: "$125.00 per year",
  },
  {
    category: "Recreational",
    amount: "$30.00 per year",
  },
];

export type FormResource = {
  title: string;
  label: string;
  href: string;
  fileInfo: string;
  /**
   * If false, the resource isn't available yet. UI will render a disabled
   * card with a contact-the-club message.
   */
  available: boolean;
};

export const registrationForms: FormResource[] = [
  {
    title: "Boxing Canada",
    label: "Registration form / membership information",
    href: "/forms/boxing-canada-membership.pdf",
    fileInfo: "PDF, 42 KB",
    available: true,
  },
  {
    title: "Boxing Canada Medical",
    label: "Medical form",
    href: "/forms/boxing-canada-medical.pdf",
    fileInfo: "PDF, 120 KB",
    available: true,
  },
];

export type WhyCard = {
  title: string;
  body: string;
};

export const whyTrainWithUs: WhyCard[] = [
  {
    title: "Learn real boxing fundamentals",
    body: "Stance, footwork, head movement, punches, combinations, and defense — taught the way the sport is taught around the world.",
  },
  {
    title: "Build strength and confidence",
    body: "Rigorous boxing workouts build character, conditioning, and self-belief, one round at a time.",
  },
  {
    title: "Supportive coaches and teammates",
    body: "Train alongside teammates who push each other and coaches who see the potential in every member who walks through the door.",
  },
  {
    title: "Free local training",
    body: "Training at the club is free. Registration fees go through Boxing Nova Scotia and Boxing Canada for certified amateur boxers.",
  },
];

export const trainingTopics: string[] = [
  "Punches and combinations",
  "Stance",
  "Footwork",
  "Head movement",
  "Parrying",
  "Defensive movements",
  "Circuit training",
  "Interval training",
  "Speed training",
  "Strength and conditioning",
];

export type ContactPerson = {
  name: string;
  role?: string;
  phone?: string;
  phoneHref?: string;
  email?: string;
  emailHref?: string;
};

export const contactPeople: ContactPerson[] = [
  {
    name: "Greg Martin",
    phone: "902.217.6694",
    phoneHref: "tel:+19022176694",
  },
  {
    name: "Daniel Martin",
    email: "boxingclub@membertou.ca",
    emailHref: "mailto:boxingclub@membertou.ca",
  },
];

